import dot from 'dot-object';
import type { Ref } from 'react';
import { parseDate } from '../../../common/functions/parse-date';
import type { CommentElementProps } from '../../comments/comment-element.component';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../tooltip/tooltip';
import type { DataGridCellRendererProps } from '../data-grid-types';
import DataGridDataCell from './data-grid-data-cell.component';

const USER_TIME_ZONE = Intl.DateTimeFormat(navigator.language).resolvedOptions().timeZone;
const DATE_FORMATTER = Intl.DateTimeFormat(navigator.language, { dateStyle: 'short', timeZone: USER_TIME_ZONE });
const TIME_FORMATTER = Intl.DateTimeFormat(navigator.language, { timeStyle: 'short', timeZone: USER_TIME_ZONE });

const domParser = new DOMParser();

function stripHTML(html: string): string {
  const doc = domParser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

function formatDisplayDate(date: Date | null): string {
  if (!date) {
    return '-';
  }

  return DATE_FORMATTER.format(date);
}

function formatDisplayTime(date: Date | null): string {
  if (!date) {
    return '-';
  }

  return TIME_FORMATTER.format(date);
}

interface HistoryCommentProps {
  comment: CommentElementProps;
}

function HistoryComment({ comment }: HistoryCommentProps) {
  const date = parseDate(comment.date);
  const formattedDate = formatDisplayDate(date);
  const formattedTime = formatDisplayTime(date);
  const safeMessage = stripHTML(comment.message);
  const tooltipText = `${formattedDate} - ${formattedTime} - ${comment.user}: ${safeMessage}`;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <p>
            <strong>
              {formattedDate} - {formattedTime} - {comment.user}:
            </strong>
            <span className="ml-1">{safeMessage}</span>
          </p>
        }
      />
      <TooltipContent side="left">{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

interface HistoryCommentsProps {
  comments: Array<CommentElementProps>;
}

function HistoryComments({ comments }: HistoryCommentsProps) {
  return comments.map((comment) => (
    <HistoryComment key={comment.id ?? `${comment.date}-${comment.user}`} comment={comment} />
  ));
}

interface DataGridDataHistoryCellProps extends DataGridCellRendererProps {
  allowCellScrollBar?: boolean;
  ref?: Ref<HTMLDivElement>;
}

function DataGridDataHistoryCell({ column, rowData, allowCellScrollBar }: DataGridDataHistoryCellProps) {
  const value: Array<CommentElementProps> = (column.valuePath ? dot.pick(column.valuePath, rowData) : []) ?? [];
  const comments = allowCellScrollBar ? value : value.slice(0, 1);

  return (
    <DataGridDataCell
      value={<HistoryComments comments={comments} />}
      allowCellScrollBar={allowCellScrollBar}
      renderAsHTML
    />
  );
}

export default DataGridDataHistoryCell;
